import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { Menu, X, Sparkles, Mail, Sun, Moon, Mic } from "lucide-react";
import api from "@/api/client";
import AskAIWidget from "@/components/AskAIWidget";
import AIStarfield from "@/components/AIStarfield";
import VoiceAIPanel from "@/components/VoiceAIPanel";
import AIFab from "@/components/AIFab";
import PublicAlgoliaSearch, { PublicSearchTrigger } from "@/components/PublicAlgoliaSearch";
import { PublicSpaceProvider } from "@/contexts/PublicSpaceContext";
import { layoutT, translateNavLinkLabel } from "@/lib/publicI18n";

const SUPPORT_EMAIL = "support@handletech.io";

export default function PublicLayout({ customDomain = false }) {
  const { slug: routeSlug } = useParams();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("docs_lang") || "en");
  const [theme, setTheme] = useState(() => localStorage.getItem("docs_theme") || "dark");
  const [aiOpen, setAiOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [aiHover, setAiHover] = useState(false);
  const [aiHoverOrigin, setAiHoverOrigin] = useState(null);
  const aiBtnRef = useRef(null);
  const openPublicSearch = useCallback(() => setSearchOpen(true), []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const url = customDomain
      ? "/public"
      : `/public?space=${encodeURIComponent(routeSlug)}`;
    api.get(url)
      .then((r) => { if (alive) setSpace(r.data.space); })
      .catch(() => { if (alive) setSpace(null); })
      .finally(() => { if (alive) setLoading(false); });

    if (!localStorage.getItem("visitor_id")) {
      localStorage.setItem("visitor_id", crypto.randomUUID());
    }
    return () => { alive = false; };
  }, [customDomain, routeSlug]);

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    localStorage.setItem("docs_lang", lang);
    return () => {
      document.documentElement.dir = "ltr";
      document.documentElement.lang = "en";
    };
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("docs_theme", theme);
  }, [theme]);

  const branding = space?.branding || {};
  const styleVars = {
    "--theme-primary": branding.primary_color || "#41B5E6",
    "--theme-bg": theme === "light" ? "#FFFFFF" : "#000000",
    "--theme-text": theme === "light" ? "#09090B" : "#FFFFFF",
    "--theme-font-heading": "'Anton', sans-serif",
  };
  const lt = layoutT(lang);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[var(--pp-bg)] text-zinc-500">{lt.loading}</div>;
  }
  if (!space) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-3 bg-[var(--pp-bg)] text-[var(--pp-text)]" data-testid="space-not-found">
        <h1 className="text-2xl font-bold">{lt.spaceNotFound}</h1>
        <p className="text-zinc-500">{lt.spaceNotFoundDesc}</p>
      </div>
    );
  }

  const activeSlug = space?.slug || routeSlug || "";
  const basePath = customDomain ? "" : `/s/${activeSlug}`;

  const headerLinks = space.settings?.header_links || [];
  const supportEmail = space.settings?.contact_email || SUPPORT_EMAIL;
  const supportsAr = (space.settings?.supported_languages || ["en"]).includes("ar");
  const primary = branding.primary_color || "#41B5E6";

  const openAI = () => setAiOpen(true);

  return (
    <PublicSpaceProvider value={{ basePath, slug: activeSlug, customDomain }}>
    <div className={`public-portal-dark ${theme === "light" ? "theme-light" : ""} min-h-screen flex flex-col`} style={styleVars}>
      {branding.custom_css ? <style dangerouslySetInnerHTML={{ __html: branding.custom_css }} /> : null}

      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[var(--pp-bg)]/80 border-b border-[var(--pp-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Link to={basePath || "/"} className="flex items-center gap-2.5 group" data-testid="public-logo">
            {(theme === "light" ? branding.logo_url : (branding.logo_url_dark || branding.logo_url)) ? (
              <img src={theme === "light" ? branding.logo_url : (branding.logo_url_dark || branding.logo_url)} alt={space.name} className="h-8" />
            ) : (
              <span
                className="w-9 h-9 rounded-md flex items-center justify-center text-[var(--pp-text)] text-base font-black"
                style={{ background: `linear-gradient(135deg, ${primary}, ${primary}99)` }}
              >
                {space.name.charAt(0)}
              </span>
            )}
            <div>
              <div className="font-display text-[15px] leading-none tracking-wider text-[var(--pp-text)]">
                {space.name}
              </div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-[var(--pp-text-dim)] font-bold mt-1">
                {lt.helpCenter}
              </div>
            </div>
          </Link>

          {space.settings?.show_search !== false && (
            <div className="hidden md:flex flex-1 max-w-md mx-6">
              <PublicSearchTrigger
                placeholder={lt.searchArticles}
                testId="public-search-input"
                onOpen={openPublicSearch}
              />
            </div>
          )}

          <nav className="ml-auto hidden md:flex items-center gap-1.5">
            {headerLinks.map((l, i) => (
              <a key={i} href={l.url}
                className="text-[11px] uppercase tracking-[0.2em] font-bold text-[var(--pp-text-muted)] hover:text-[var(--pp-text)] px-3 py-2 transition-colors">
                {translateNavLinkLabel(l, lang)}
              </a>
            ))}

            {supportsAr && (
              <div className="flex items-center bg-[var(--pp-surface)] border border-[var(--pp-border)] rounded-full overflow-hidden ml-1 p-0.5" data-testid="lang-switcher">
                <button onClick={() => setLang("en")} data-testid="lang-en"
                  className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full transition-colors ${
                    lang === "en" ? "bg-[var(--pp-text)] text-[var(--pp-bg)]" : "text-[var(--pp-text-muted)] hover:text-[var(--pp-text)]"
                  }`}>EN</button>
                <button onClick={() => setLang("ar")} data-testid="lang-ar"
                  className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full transition-colors ${
                    lang === "ar" ? "bg-[var(--pp-text)] text-[var(--pp-bg)]" : "text-[var(--pp-text-muted)] hover:text-[var(--pp-text)]"
                  }`}>AR</button>
              </div>
            )}

            {/* Theme toggle */}
            <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="theme-toggle"
              title={theme === "dark" ? lt.switchLight : lt.switchDark}
              className="ml-1 w-9 h-9 flex items-center justify-center bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[var(--pp-border-hover)] rounded-full text-[var(--pp-text-muted)] hover:text-[var(--pp-text)] transition-colors">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>


            <button onClick={openAI} data-testid="ask-ai-btn" title={lt.askAi}
              ref={aiBtnRef}
              onMouseEnter={() => {
                const rect = aiBtnRef.current?.getBoundingClientRect();
                if (rect) setAiHoverOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                setAiHover(true);
              }}
              onMouseLeave={() => setAiHover(false)}
              className="ml-1 flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-[var(--pp-text)] rounded-full transition-all hover:opacity-90 hover:scale-[1.04]"
              style={{
                background: `linear-gradient(135deg, ${primary}, ${primary}dd)`,
                boxShadow: aiHover
                  ? `0 0 0 4px ${primary}33, 0 0 30px ${primary}aa, 0 0 60px ${primary}77`
                  : `0 0 18px ${primary}55`,
              }}>
              <Sparkles className={`w-3.5 h-3.5 transition-transform ${aiHover ? "scale-125 rotate-12" : ""}`} />
              {lt.askAi}
            </button>

            <button onClick={() => setVoiceOpen(true)} data-testid="ask-voice-btn" title={lt.voiceAi}
              className="ml-1 w-10 h-10 flex items-center justify-center rounded-full transition-all hover:scale-[1.05] border"
              style={{
                background: `linear-gradient(135deg, ${primary}33, transparent)`,
                borderColor: `${primary}66`,
                color: primary,
                boxShadow: `0 0 14px ${primary}44`,
              }}>
              <Mic className="w-4 h-4" />
            </button>

            <a href={`mailto:${supportEmail}`} data-testid="contact-support-btn"
              className="ml-1 flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-[var(--pp-text)] border border-[var(--pp-border)] hover:border-white hover:bg-white hover:text-black rounded-full transition-colors">
              <Mail className="w-3.5 h-3.5" />
              {lt.support}
            </a>
          </nav>

          <button onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden ml-auto p-2 text-[var(--pp-text)]" data-testid="mobile-menu-btn" aria-label={lt.menu}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-[var(--pp-border)] p-4 space-y-3 bg-[var(--pp-bg)]">
            {space.settings?.show_search !== false && (
              <PublicSearchTrigger
                placeholder={lt.searchShort}
                testId="public-search-input-mobile"
                onOpen={() => { setMenuOpen(false); openPublicSearch(); }}
              />
            )}
            {headerLinks.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {headerLinks.map((l, i) => (
                  <a key={i} href={l.url}
                    className="px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold border border-[var(--pp-border)] rounded-full text-[var(--pp-text-muted)] hover:text-[var(--pp-text)]">
                    {translateNavLinkLabel(l, lang)}
                  </a>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              {supportsAr && (
                <>
                  <button onClick={() => setLang("en")} className={`flex-1 py-2 text-xs font-black rounded-full ${lang === "en" ? "bg-white text-black" : "bg-[var(--pp-surface)] text-[var(--pp-text)] border border-[var(--pp-border)]"}`}>EN</button>
                  <button onClick={() => setLang("ar")} className={`flex-1 py-2 text-xs font-black rounded-full ${lang === "ar" ? "bg-white text-black" : "bg-[var(--pp-surface)] text-[var(--pp-text)] border border-[var(--pp-border)]"}`}>AR</button>
                </>
              )}
            </div>
            <button onClick={openAI} className="w-full py-2 text-xs font-black uppercase tracking-wider text-[var(--pp-text)] rounded-full"
              style={{ background: `linear-gradient(135deg, ${primary}, ${primary}dd)` }}>{lt.askAi}</button>
            <a href={`mailto:${supportEmail}`} className="block w-full text-center py-2 text-xs font-black uppercase tracking-wider border border-[var(--pp-border)] text-[var(--pp-text)] rounded-full">{lt.contactSupport}</a>
          </div>
        )}
      </header>

      <div className="flex-1">
        <Outlet context={{ space, slug: activeSlug, basePath, customDomain, lang, setLang, supportEmail, primary, theme, openPublicSearch }} />
      </div>

      <footer className="border-t border-[var(--pp-border)] py-10 mt-auto bg-[var(--pp-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-md flex items-center justify-center text-[var(--pp-text)] text-sm font-black"
              style={{ background: `linear-gradient(135deg, ${primary}, ${primary}99)` }}>
              {space.name.charAt(0)}
            </span>
            <div>
              <div className="font-display text-sm tracking-wider text-[var(--pp-text)]">{space.name}</div>
              <p className="text-xs text-[var(--pp-text-dim)]">{space.settings?.footer_text || `© ${space.name}`}</p>
            </div>
          </div>
          <div className="text-xs text-[var(--pp-text-dim)]">
            {lt.poweredBy} <span className="font-display tracking-wider text-[var(--pp-text)]">Handle Docs</span>
          </div>
        </div>
      </footer>

      <AskAIWidget
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        spaceSlug={activeSlug}
        lang={lang}
        primary={primary}
        theme={theme}
      />

      {/* Hover burst — radiates from the Ask AI button */}
      <AIStarfield
        active={aiHover && !aiOpen}
        mode="burst"
        origin={aiHoverOrigin}
        color={primary}
        density={1}
        zIndex={70}
      />

      <VoiceAIPanel
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        spaceSlug={activeSlug}
        spaceName={space.name}
        lang={lang}
        primary={primary}
        theme={theme}
      />

      {/* Floating speed-dial FAB → Chat / Voice */}
      <AIFab
        primary={primary}
        lang={lang}
        onChat={() => setAiOpen(true)}
        onVoice={() => setVoiceOpen(true)}
      />

      {space.settings?.show_search !== false && (
        <PublicAlgoliaSearch
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          slug={activeSlug}
          basePath={basePath}
          lang={lang}
          setLang={setLang}
          primary={primary}
        />
      )}
    </div>
    </PublicSpaceProvider>
  );
}
